import React, { useCallback, useEffect, useState } from "react";
import { Activity, AlertCircle, Loader2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/* Phase 8 — workspace usage panel inside the Settings modal.
 * Owner / Admin only.
 *
 * The Settings modal already loads workspace.role; we use that to gate
 * rendering. Members see the section with a disabled / informational state.
 */

const PLAN_LABELS = {
  free: "Free trial",
  team: "Team",
  team_plus: "Team Plus",
  grandfathered: "Grandfathered",
};

function fmtCents(c) {
  const v = Number(c || 0);
  return `$${(v / 100).toFixed(4)}`;
}
function fmtTokens(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

function TrialProgress({ trial }) {
  const pct = Math.min(100, Math.max(0, Number(trial?.percent_used || 0)));
  const tier =
    pct >= 100 ? "red" : pct >= 80 ? "orange" : pct >= 50 ? "yellow" : "green";
  const fillColor = {
    green: "bg-[#3F8C5A]",
    yellow: "bg-[#D9A23A]",
    orange: "bg-[#D96C4A]",
    red: "bg-[#A8351F]",
  }[tier];
  const trackColor = "bg-[#E8E2D2]";
  return (
    <div data-testid="usage-trial-progress" data-tier={tier}>
      <div className={`h-2 w-full rounded-full overflow-hidden ${trackColor}`}>
        <div
          className={`h-full ${fillColor} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-3 font-sans text-xs text-[#7A7571]">
        <span>
          Used{" "}
          <span
            className="font-medium text-[#2C2A29]"
            data-testid="usage-trial-used"
          >
            {fmtCents(trial?.used_cents)}
          </span>{" "}
          of {fmtCents(trial?.initial_cents)}
        </span>
        <span data-testid="usage-trial-remaining">
          {fmtCents(trial?.remaining_cents)} remaining
        </span>
      </div>
    </div>
  );
}

export default function UsagePanel({ workspace }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAllChats, setShowAllChats] = useState(false);
  const isAdminOrOwner =
    workspace?.role === "owner" || workspace?.role === "admin";
  const isSuperadmin = !!user?.is_superadmin;

  const fetchUsage = useCallback(async () => {
    if (!workspace?.id || !isAdminOrOwner) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/workspaces/${workspace.id}/usage`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [workspace?.id, isAdminOrOwner]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  if (!isAdminOrOwner) {
    return (
      <section
        className="space-y-3 mt-6"
        data-testid="settings-section-usage"
      >
        <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
          Usage
        </h3>
        <div
          className="rounded-xl border border-[#DCD7CE] bg-[#FBF8F2] px-4 py-3.5 font-sans text-sm text-[#7A7571]"
          data-testid="usage-readonly-note"
        >
          Workspace usage is visible to <strong>Owner</strong> and{" "}
          <strong>Admin</strong> only.
        </div>
      </section>
    );
  }

  const plan = data?.plan || workspace?.plan || "free";
  const trial = data?.trial;
  const lifetime = data?.lifetime;
  const perChat = data?.per_chat || [];
  const perUser = data?.per_user || [];
  const displayPerChat = showAllChats ? perChat : perChat.slice(0, 20);

  return (
    <section
      className="space-y-3 mt-6"
      data-testid="settings-section-usage"
      data-plan={plan}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
          Usage
        </h3>
        <button
          type="button"
          onClick={fetchUsage}
          disabled={loading}
          data-testid="usage-refresh-button"
          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-sans font-medium text-[#2C2A29] hover:bg-[#E8E2D2] disabled:opacity-60 transition-colors"
        >
          {loading ? (
            <Loader2 size={11} className="animate-spin" />
          ) : (
            <RefreshCw size={11} strokeWidth={2} />
          )}
          Refresh
        </button>
      </div>

      {/* Plan + trial card */}
      <div
        className="rounded-xl border border-[#DCD7CE] bg-[#FBF8F2] px-4 py-4"
        data-testid="usage-plan-card"
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-[#7A4A2C]" />
            <span
              className="font-sans text-sm font-medium text-[#2C2A29]"
              data-testid="usage-plan-label"
            >
              Plan: {PLAN_LABELS[plan] || plan}
            </span>
          </div>
          {isSuperadmin ? (
            <span
              className="font-sans text-[10px] uppercase tracking-[0.15em] text-[#7A7571] font-semibold"
              data-testid="usage-markup"
            >
              Markup: {data?.markup_rate_percent ?? workspace?.markup_rate_percent ?? 50}%
            </span>
          ) : null}
        </div>
        {plan === "free" ? (
          trial ? (
            <TrialProgress trial={trial} />
          ) : (
            <div className="text-[12px] font-sans text-[#7A7571]">
              Loading trial…
            </div>
          )
        ) : plan === "grandfathered" ? (
          <div
            className="font-sans text-xs text-[#7A7571]"
            data-testid="usage-grandfathered-note"
          >
            Internal workspace — no usage limits, no billing.
          </div>
        ) : (
          <div
            className="font-sans text-sm text-[#2C2A29]"
            data-testid="usage-prepaid-balance"
          >
            Prepaid balance: <strong>{fmtCents(data?.prepaid?.balance_cents)}</strong>{" "}
            <span className="text-[#7A7571] text-xs">— top-up via Stripe coming soon</span>
          </div>
        )}
      </div>

      {/* Lifetime stats */}
      <div
        className="grid grid-cols-3 gap-2"
        data-testid="usage-lifetime"
      >
        <Stat
          label="Tokens in"
          value={fmtTokens(lifetime?.input_tokens)}
          testid="usage-lifetime-tokens-in"
        />
        <Stat
          label="Tokens out"
          value={fmtTokens(lifetime?.output_tokens)}
          testid="usage-lifetime-tokens-out"
        />
        <Stat
          label="Cost"
          value={fmtCents(lifetime?.customer_cost_cents)}
          testid="usage-lifetime-customer"
        />
      </div>

      {/* By chat */}
      <div
        className="rounded-xl border border-[#DCD7CE] overflow-hidden"
        data-testid="usage-per-chat-card"
      >
        <div className="px-4 py-2 bg-[#F3F1EC] border-b border-[#DCD7CE] font-sans text-[10px] uppercase tracking-[0.16em] text-[#7A7571] font-semibold">
          By chat
        </div>
        {perChat.length === 0 ? (
          <div className="px-4 py-3 font-sans text-xs text-[#A5A29E]">
            No chat activity yet.
          </div>
        ) : (
          <ul
            className="divide-y divide-[#EFEAE0]"
            data-testid="usage-per-chat-list"
          >
            {displayPerChat.map((c) => (
              <li
                key={c.chat_id}
                className="flex items-center justify-between gap-3 px-4 py-2"
                data-testid={`usage-per-chat-row-${c.chat_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-sans text-sm text-[#2C2A29] truncate">
                    {c.chat_title || "Untitled"}
                  </div>
                  <div className="font-sans text-[11px] text-[#A5A29E]">
                    {c.message_count} message{c.message_count === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="font-mono text-sm text-[#2C2A29] tabular-nums">
                  {fmtCents(c.customer_cost_cents)}
                </div>
              </li>
            ))}
          </ul>
        )}
        {perChat.length > 20 ? (
          <button
            type="button"
            onClick={() => setShowAllChats((v) => !v)}
            data-testid="usage-per-chat-toggle"
            className="w-full px-4 py-2 font-sans text-xs font-medium text-[#7A4A2C] hover:bg-[#F3F1EC] transition-colors"
          >
            {showAllChats
              ? "Show top 20 only"
              : `Show all ${perChat.length} chats`}
          </button>
        ) : null}
      </div>

      {/* By user */}
      <div
        className="rounded-xl border border-[#DCD7CE] overflow-hidden"
        data-testid="usage-per-user-card"
      >
        <div className="px-4 py-2 bg-[#F3F1EC] border-b border-[#DCD7CE] font-sans text-[10px] uppercase tracking-[0.16em] text-[#7A7571] font-semibold">
          By user
        </div>
        {perUser.length === 0 ? (
          <div className="px-4 py-3 font-sans text-xs text-[#A5A29E]">
            No user activity yet.
          </div>
        ) : (
          <ul
            className="divide-y divide-[#EFEAE0]"
            data-testid="usage-per-user-list"
          >
            {perUser.map((u) => (
              <li
                key={u.user_id || u.email}
                className="flex items-center justify-between gap-3 px-4 py-2"
                data-testid={`usage-per-user-row-${u.user_id || u.email}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-sans text-sm text-[#2C2A29] truncate">
                    {u.display_name}
                  </div>
                  <div className="font-sans text-[11px] text-[#A5A29E] truncate">
                    {u.email} · {u.message_count} prompt
                    {u.message_count === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="font-mono text-sm text-[#2C2A29] tabular-nums">
                  {fmtCents(u.customer_cost_cents)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {data?.updated_at ? (
        <p
          className="font-sans text-[10px] text-[#A5A29E] flex items-center gap-1"
          data-testid="usage-updated-at"
        >
          <AlertCircle size={10} /> Last refreshed{" "}
          {new Date(data.updated_at).toLocaleString()}
        </p>
      ) : null}
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div
      className="rounded-xl border border-[#DCD7CE] bg-white px-3 py-2.5"
      data-testid={testid}
    >
      <div className="font-sans text-[10px] uppercase tracking-[0.14em] text-[#7A7571]">
        {label}
      </div>
      <div className="font-serif text-lg text-[#2C2A29] tabular-nums">
        {value}
      </div>
    </div>
  );
}
